/**
 * Read-only view of docs/loop/config.yaml on the /agents page.
 *
 * Tuning constants (slot interval, throttles, per-agent cooldowns + caps)
 * live in the repo and edits land via PR. This panel surfaces the live
 * values so they're visible at a glance without opening a file. Backed by
 * GET /api/agents/config which parses the yaml server-side.
 */
import { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { LoopConfig } from '../../types/agents';

interface LoopConfigPanelProps {
  config: LoopConfig | null;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function KeyValueTable({ data }: { data: Record<string, unknown> }) {
  const rows = Object.entries(data);
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        (empty)
      </Typography>
    );
  }
  return (
    <Table size="small">
      <TableBody>
        {rows.map(([k, v]) => (
          <TableRow key={k}>
            <TableCell
              sx={{ fontFamily: 'monospace', fontSize: '0.8em', border: 0, py: 0.25, width: '40%' }}
            >
              {k}
            </TableCell>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8em', border: 0, py: 0.25 }}>
              {formatValue(v)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function LoopConfigPanel({ config }: LoopConfigPanelProps) {
  const [expanded, setExpanded] = useState(false);

  if (!config) {
    return null;
  }

  const agentNames = Object.keys(config.agents).sort();

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-testid="loop-config-panel">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Loop config (read-only)
        </Typography>
        <Chip
          label={config.source_path}
          size="small"
          variant="outlined"
          sx={{ fontFamily: 'monospace' }}
        />
        <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Tuning constants edited via PR; this panel reflects the live yaml.
      </Typography>
      <Collapse in={expanded}>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              dispatch
            </Typography>
            <KeyValueTable data={config.dispatch} />
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              throttles
            </Typography>
            <KeyValueTable data={config.throttles} />
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              agents
            </Typography>
            {agentNames.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                (no agent blocks)
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {agentNames.map((name) => (
                  <Box key={name}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {name}
                    </Typography>
                    <KeyValueTable data={config.agents[name]} />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </Collapse>
    </Paper>
  );
}
